import { config } from '../config';
import type { GameProfile } from '../types';
import type { RevealAnimation } from './RevealAnimation';

export interface DrawSequencerOptions {
  profile: GameProfile;
  /** One RevealAnimation per machine. For single-machine games length=1; Sicbo length=3. */
  reveals: RevealAnimation[];
  /** Stagger between machines for Sicbo (seconds). 0 = simultaneous. */
  sicboStagger?: number;
  /** Called when each ball completes its reveal (slot-fill UI). */
  onBallRevealed?: (slot: number, id: number) => void;
  /** Called at the very start, before intro churn. */
  onDrawStart?: (results: number[]) => void;
  /** Called when all reveals are done (overlay celebration etc.). */
  onDrawComplete?: (results: number[]) => void;
}

/**
 * Orchestrates a single draw cycle: intro churn → reveal(s) → outro.
 * Public API: `run(results)`. Returns a promise resolving when everything (incl. outro) finishes.
 */
export class DrawSequencer {
  private readonly opts: DrawSequencerOptions;
  private running = false;

  constructor(opts: DrawSequencerOptions) {
    this.opts = opts;
  }

  async run(results: number[]): Promise<void> {
    if (this.running) {
      console.warn('DrawSequencer.run() called while a draw is already in progress; ignored.');
      return;
    }
    this.validate(results);
    this.running = true;
    try {
      this.opts.onDrawStart?.(results);
      await sleep(config.sequencer.introChurnSec * 1000);
      await this.playReveals(results);
      await sleep(config.sequencer.outroSec * 1000);
      this.opts.onDrawComplete?.(results);
    } finally {
      this.running = false;
    }
  }

  private async playReveals(results: number[]): Promise<void> {
    const stagger = (this.opts.sicboStagger ?? config.sequencer.sicboStaggerSec) * 1000;
    const tasks: Promise<void>[] = [];

    for (let i = 0; i < results.length; i++) {
      const id = results[i]!;
      const reveal = this.opts.reveals[i];
      if (!reveal) {
        throw new Error(`DrawSequencer: no RevealAnimation for slot ${i} (results.length=${results.length})`);
      }
      const slotIndex = i;
      const start = stagger * i;
      tasks.push(
        (async () => {
          if (start > 0) await sleep(start);
          await reveal.play(id);
          this.opts.onBallRevealed?.(slotIndex, id);
        })(),
      );
    }
    await Promise.all(tasks);
  }

  private validate(results: number[]): void {
    if (results.length !== this.opts.profile.resultsPerDraw) {
      throw new Error(
        `Profile "${this.opts.profile.id}" expects ${this.opts.profile.resultsPerDraw} result(s); got ${results.length}.`,
      );
    }
    const valid = new Set(this.opts.profile.ballIds);
    for (const r of results) {
      if (!valid.has(r)) {
        throw new Error(`Result ${r} not in profile "${this.opts.profile.id}" ballIds.`);
      }
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}
